import { Icon } from "../Icon";
import React, {
  useState,
  forwardRef,
  useId,
  useImperativeHandle,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";
import {
  Z_INDEX,
  COLOR_CONSTANTS,
  THEME_TRANSITIONS,
} from "../../constants/styles";
import { SidebarItemButton } from "./components";
import ToggleButtonGroup from "../ToggleButtonGroup";
import { SidebarBase } from "./components/SidebarBase";
import { SidebarOverflowMenu } from "./components/SidebarOverflowMenu";
import { SidebarHeader } from "./components/SidebarHeader";
import { SidebarDivider } from "./components/SidebarDivider";
import { SidebarIconButton } from "./components/SidebarIconButton";
import { SidebarLocaleToggle } from "./components/SidebarLocaleToggle";
import { SidebarSectionLabel } from "./components/SidebarSectionLabel";
import { SidebarProfileSection } from "./components/SidebarProfileSection";
import {
  Box,
  Stack,
  IconButton,
  Collapse,
  ToggleButton,
  Tooltip,
} from "@mui/material";
import { SIDEBAR_BASE_WIDTH } from "src/store/sidebarLayoutStore";

export type ApplicationSideBarItem = {
  id: string;
  icon: {
    component: React.ReactNode;
    name: string;
  };
  name: string;
  onClick?: () => void;
  component: React.ReactNode;
};

export type ApplicationSideBarModalItem = {
  id: string;
  name: string;
  component: React.ReactNode;
};

/** 레일의 두 보기 — `2D`·`3D` 토글이 고르는 값. */
type ApplicationSideBarMode = "2D" | "3D";

/**
 * 풀 기능 사이드바 props — 2D/3D 모드 토글 + 펼침/접힘 패널 + 별도 모달 슬롯.
 *
 * 크기: 사이드바 72px 고정, 패널 펼침 시 추가 320px.
 * 단순 목록 사이드바는 {@link ProjectSideBar} 를 쓴다.
 * ref 로 `ApplicationSideBarRef` 를 노출 — 프로그래밍 방식 패널 제어 가능.
 */
export type ApplicationSideBarProps = {
  name: string;
  items: ApplicationSideBarItem[];
  modal?: ApplicationSideBarModalItem[];
  onHomeClick?: () => void;
  mode: ApplicationSideBarMode;
  onModeChange?: (mode: ApplicationSideBarMode) => void;
  /**
   * 지금 **고를 수 없는 보기**와 그 까닭. 지정된 쪽 버튼은 네이티브 `disabled` 가 되고, 까닭은 포인터를 올리면 툴팁으로
   * 뜨며 스크린리더는 그 버튼의 접근 설명으로 읽는다.
   *
   * 버튼을 숨기지 않고 막는 것은 레일의 버튼 수가 화면 상태에 따라 바뀌지 않게 하려는 것이다 — 숨기면 그 아래 항목들이
   * 위아래로 움직인다. 주지 않으면 종전과 같이 두 버튼 모두 고를 수 있고, 그리는 트리도 종전과 같다.
   */
  unavailableMode?: { mode: ApplicationSideBarMode; reason: string };
  /**
   * 패널 상태가 바뀔 때마다(마운트 포함) 호출된다.
   *
   * `totalWidth` 는 사이드바 전체 폭(px) — 레일 몫 `SIDEBAR_BASE_WIDTH` + 열린 일반/모달 패널 몫.
   * 패널 몫은 패널 영역 렌더와 같은 변수(`panelWidth` 반영)에서 나온다. 레일 자체는 `SidebarBase` 의
   * 기본 폭으로 그려져 레일 몫과 값만 같다(같은 상수를 읽지 않는다).
   * 폭이 필요한 소비처는 이 값을 그대로 쓰고 패널 상태로 폭을 다시 계산하지 않는다.
   */
  onPanelChange?: (
    isExpanded: boolean,
    isHidden: boolean,
    isModalOpen: boolean,
    totalWidth: number
  ) => void;
  /** true 면 사이드바 버튼을 잠근다(홈·뒤로가기·매뉴얼은 disabled, 아이템은 로딩 스피너 표시) — 로딩 중 조작 잠금용. 2D/3D 모드 토글은 로딩 중에도 자유롭게 전환 가능(3D는 2D 체류 중에도 백그라운드 로드) */
  loadingLocked?: boolean;
  /** loadingLocked 상태에서도 클릭을 허용할(잠금 예외) item id 목록 */
  lockExemptItemIds?: string[];
  /**
   * 펼침 패널·모달 패널의 폭(px). 기본 320.
   *
   * 패널 안에 표를 놓는 화면(단지 토공)이 320 에서 열을 잘라 먹어 열었다. 기본값을 그대로 두면
   * 지정하지 않은 소비자의 렌더 결과는 종전과 같다 — 폭은 이 두 패널의 `width` 로만 흐른다.
   */
  panelWidth?: number;
};

export type ApplicationSideBarRef = {
  handleItemClick: (itemId: string) => void;
  handleClose: () => void;
  handleShow: () => void;
  openItem: (itemId: string) => void;
  closeItem: () => void;
  openModal: (modalId: string) => void;
  closeModal: () => void;
};

const PANEL_WIDTH = 320;
const MENU_ITEM_GAP = 12;

export const ApplicationSideBar = forwardRef<
  ApplicationSideBarRef,
  ApplicationSideBarProps
>(
  (
    {
      name,
      items,
      modal = [],
      onHomeClick,
      mode,
      onModeChange,
      unavailableMode,
      onPanelChange,
      loadingLocked = false,
      lockExemptItemIds = [],
      panelWidth = PANEL_WIDTH,
    },
    ref
  ) => {
    const [activeItemId, setActiveItemId] = useState<string | null>(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isHidden, setIsHidden] = useState(false);
    const [activeModalId, setActiveModalId] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [visibleItemCount, setVisibleItemCount] = useState(items.length);

    const menuAreaRef = useRef<HTMLDivElement>(null);

    const onPanelChangeRef = useRef(onPanelChange);
    onPanelChangeRef.current = onPanelChange;

    useLayoutEffect(() => {
      const area = menuAreaRef.current;
      if (!area) return;

      const measure = () => {
        if (area.clientHeight === 0) {
          setVisibleItemCount(items.length);
          return;
        }
        const first = area.children[0] as HTMLElement | undefined;
        const second = area.children[1] as HTMLElement | undefined;
        const itemHeight = first?.offsetHeight || 40;
        const gap =
          first && second
            ? Math.max(0, second.offsetTop - first.offsetTop - itemHeight)
            : MENU_ITEM_GAP;
        const fit = Math.floor((area.clientHeight + gap) / (itemHeight + gap));
        setVisibleItemCount(
          fit >= items.length ? items.length : Math.max(0, fit - 1)
        );
      };

      measure();
      const observer = new ResizeObserver(measure);
      observer.observe(area);
      return () => observer.disconnect();
    }, [items.length]);

    const openItem = (itemId: string) => {
      setActiveItemId(itemId);
      setIsExpanded(true);
      setIsHidden(false);
    };

    const closeItem = () => {
      setActiveItemId(null);
      setIsExpanded(false);
      setIsHidden(false);
    };

    const handleItemClick = (itemId: string) => {
      if (activeItemId === itemId) {
        closeItem();
      } else {
        openItem(itemId);
      }
    };

    const handleClose = () => {
      setIsHidden(true);
    };

    const handleModalClose = () => {
      setIsHidden(true);
    };

    const handleShow = () => {
      setIsHidden(false);
    };

    const openModal = (modalId: string) => {
      setActiveModalId(modalId);
      setIsModalOpen(true);
      setIsHidden(false);
    };

    const closeModal = () => {
      setActiveModalId(null);
      setIsModalOpen(false);
    };

    // 패널 영역 렌더와 onPanelChange 의 totalWidth 가 같은 패널 폭을 읽는다 — 소비처가 재계산하지 않도록
    const generalMenuWidth =
      isExpanded && !isHidden && !activeModalId ? panelWidth : 0;
    const modalWidth = activeModalId && !isHidden ? panelWidth : 0;
    const totalWidth = SIDEBAR_BASE_WIDTH + generalMenuWidth + modalWidth;

    useLayoutEffect(() => {
      onPanelChangeRef.current?.(isExpanded, isHidden, isModalOpen, totalWidth);
    }, [isExpanded, isHidden, isModalOpen, totalWidth]);

    // 사이드바나 모달이 열릴 때 body 스크롤 방지
    useEffect(() => {
      if (isExpanded || isModalOpen) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "unset";
      }

      // 컴포넌트 언마운트 시 스크롤 복원
      return () => {
        document.body.style.overflow = "unset";
      };
    }, [isExpanded, isModalOpen]);

    useImperativeHandle(ref, () => ({
      handleItemClick,
      handleClose,
      handleShow,
      openItem,
      closeItem,
      openModal,
      closeModal,
    }));

    const activeItem = items.find((item) => item.id === activeItemId);
    const activeModal = modal.find(
      (modalItem) => modalItem.id === activeModalId
    );

    const visibleItems = items.slice(0, visibleItemCount);
    const overflowItems = items.slice(visibleItemCount);

    const unavailableReasonId = useId();
    /** 그 보기의 버튼에 다는 잠금 — 고를 수 있는 보기면 아무것도 달지 않는다. */
    const lockOf = (target: ApplicationSideBarMode) =>
      unavailableMode?.mode === target
        ? { disabled: true, "aria-describedby": unavailableReasonId }
        : undefined;

    const modeToggle = (
      <Box sx={unavailableMode ? lockedToggleButtonBox : toggleButtonBox}>
        <ToggleButtonGroup
          hasDropshadow={false}
          orientation="vertical"
          size="medium"
          value={mode}
          onChange={(_, value) => {
            if (value !== null) onModeChange?.(value as ApplicationSideBarMode);
          }}
        >
          <ToggleButton value="2D" {...lockOf("2D")}>
            2D
          </ToggleButton>
          <ToggleButton value="3D" {...lockOf("3D")}>
            3D
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
    );

    return (
      <Box sx={container}>
        {/* 메인 사이드바 */}
        <SidebarBase>
          {/* 홈 링크 */}
          <SidebarHeader onHomeClick={onHomeClick} disabled={loadingLocked} />
          <Box sx={backButtonBox}>
            <IconButton
              sx={backButton}
              disabled={loadingLocked}
              onClick={() => (window.location.href = "/projects-list")}
            >
              <Icon name="16/arrow/left" size={16} clickable />
            </IconButton>
          </Box>

          <SidebarDivider />
          {unavailableMode ? (
            <>
              {/*
                툴팁은 버튼이 아니라 **버튼 묶음 상자**를 감싼다. 비활성 버튼은 포인터 이벤트를 받지 않아 hover 를 받을 자리가
                따로 필요한데, 버튼을 span 으로 감싸면 `ToggleButtonGroup` 의 `:first-of-type`·`:last-of-type` 스타일(모서리·
                경계선)이 그 span 안에서 다시 계산돼 깨진다. `describeChild` 는 사유가 상자의 이름(`aria-label`)으로 실리지
                않게 한다 — 사유는 아래 버튼의 접근 설명 한 길로만 전한다(`SidebarItemButton` 의 `disabledReason` 과 같다).
              */}
              <Tooltip
                title={unavailableMode.reason}
                placement="right"
                arrow
                describeChild
              >
                {modeToggle}
              </Tooltip>
              {/* 막힌 버튼의 `aria-describedby` 가 가리키는 사유 — `hidden` 이어도 직접 참조한 내용은 접근 설명으로 계산된다. */}
              <span id={unavailableReasonId} hidden>
                {unavailableMode.reason}
              </span>
            </>
          ) : (
            modeToggle
          )}
          <SidebarDivider />
          <SidebarSectionLabel>APP</SidebarSectionLabel>
          <Stack
            ref={menuAreaRef}
            direction="column"
            spacing={1.5}
            alignItems="center"
            sx={mainMenuItems}
          >
            {visibleItems.map((item) => (
              <SidebarItemButton
                key={item.id}
                icon={{
                  component: item.icon.component,
                  name: item.icon.name,
                }}
                mode={activeItemId === item.id ? "on" : "off"}
                loading={loadingLocked && !lockExemptItemIds.includes(item.id)}
                onClick={() => {
                  closeModal();
                  item.onClick?.();
                  handleItemClick(item.id);
                }}
              />
            ))}
            {overflowItems.length > 0 && (
              <SidebarOverflowMenu
                items={overflowItems}
                activeItemId={activeItemId}
                loadingLocked={loadingLocked}
                lockExemptItemIds={lockExemptItemIds}
                onSelect={(item) => {
                  closeModal();
                  item.onClick?.();
                  handleItemClick(item.id);
                }}
              />
            )}
          </Stack>
          <Stack
            direction="column"
            spacing={1}
            alignItems="center"
            sx={bottomIcons}
          >
            <SidebarIconButton
              opacity={0.5}
              disabled={loadingLocked}
              icon={<Icon name="24/nav/basic/manual" size={24} clickable />}
              onClick={() => window.open("/manual", "_blank")}
            />
            <SidebarLocaleToggle />
          </Stack>
          <SidebarDivider variant="thick" />
          <SidebarProfileSection name={name} />
        </SidebarBase>

        {/* 패널 영역 */}
        <Box
          sx={{
            ...panelContainer,
            width: generalMenuWidth + modalWidth,
            transition: `width ${THEME_TRANSITIONS.duration.standard}ms ${THEME_TRANSITIONS.easing.easeIn}`,
          }}
        >
          {/* 일반 메뉴 영역 */}
          <Box
            sx={{
              ...panelBase,
              width: panelWidth,
              border:
                isExpanded && !isHidden && !activeModalId
                  ? `1px solid ${COLOR_CONSTANTS.divider.divider}`
                  : "none",
              display:
                isExpanded && !isHidden && !activeModalId ? "flex" : "none",
            }}
          >
            <Box sx={generalMenuHeader}>
              <IconButton sx={iconButtonNoPadding} onClick={handleClose}>
                <Icon
                  name="24/nav/basic/left-panel-close"
                  size={20}
                  clickable
                />
              </IconButton>
            </Box>
            <Box sx={generalMenuContent}>{activeItem?.component}</Box>
          </Box>

          {/* 모달 영역 */}
          <Box
            sx={{
              ...panelBase,
              width: panelWidth,
              zIndex: isExpanded && activeItemId ? Z_INDEX.sideBarModal : 1,
              border:
                isModalOpen && !isHidden
                  ? `1px solid ${COLOR_CONSTANTS.divider.divider}`
                  : "none",
              display: isModalOpen && !isHidden ? "flex" : "none",
            }}
          >
            <Box sx={modalHeader}>
              <Box sx={modalHeaderTitle}></Box>
              <IconButton sx={iconButtonNoPadding} onClick={handleModalClose}>
                <Icon
                  name="24/nav/basic/left-panel-close"
                  size={20}
                  clickable
                />
              </IconButton>
            </Box>
            <Box sx={modalContent}>{activeModal?.component}</Box>
          </Box>

          {isHidden && (activeItemId || activeModalId) && (
            <Box sx={showButton} onClick={handleShow}>
              <Icon name="20/arrow/right" size={24} clickable />
            </Box>
          )}
        </Box>
      </Box>
    );
  }
);

ApplicationSideBar.displayName = "ApplicationSideBar";

const container = {
  display: "flex",
  height: "100vh",
  overflow: "hidden",
  position: "relative",
  zIndex: Z_INDEX.dropdown,
};

const panelContainer = {
  display: "flex",
  height: "100vh",
  overflow: "hidden",
  position: "relative",
};

const backButtonBox = {
  padding: "12px 0",
  width: "100%",
  display: "flex",
  justifyContent: "center",
};

const backButton = {
  width: 36,
  height: 36,
  background: COLOR_CONSTANTS._components.switch.knobFillEnabled,
  borderRadius: "4px",
};

const toggleButtonBox = {
  padding: "11px 0",
  opacity: 0.8,
};

/**
 * 한쪽 보기가 막힌 묶음 상자. 비활성 버튼이 포인터 이벤트를 흘려보내 제 커서를 세우지 못하므로 커서를 이 상자에 둔다 —
 * 고를 수 있는 쪽 버튼은 제 커서(pointer)가 그대로 선다.
 */
const lockedToggleButtonBox = {
  ...toggleButtonBox,
  cursor: "not-allowed",
};

const mainMenuItems = {
  flexGrow: 1,
  width: "100%",
  minHeight: 40,
  overflow: "hidden",
};

const bottomIcons = {
  mb: 2,
};

const generalMenuHeader = {
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  padding: "10px 16px",
};

const panelBase = {
  width: PANEL_WIDTH,
  height: "100vh",
  flexDirection: "column",
  borderBottom: "1px solid #E0E0E0",
  backgroundColor: "#f5f5f5",
};

const generalMenuContent = {
  flex: 1,
  height: "calc(100vh - 53px)",
  overflow: "auto",
  "&::-webkit-scrollbar": {
    display: "none",
  },
  scrollbarWidth: "none",
  msOverflowStyle: "none",
};

const modalHeader = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "10px 16px",
};

const modalHeaderTitle = {
  fontWeight: 500,
  fontSize: "14px",
};

const iconButtonNoPadding = {
  padding: 0,
};

const modalContent = {
  flex: 1,
  height: "calc(100vh - 53px)",
  overflow: "auto",
  "&::-webkit-scrollbar": {
    display: "none",
  },
  scrollbarWidth: "none",
  msOverflowStyle: "none",
};

const showButton = {
  position: "fixed",
  left: "72px",
  top: "50%",
  transform: "translateY(-50%)",
  width: 24,
  height: 72,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#FFFFFF",
  borderLeft: "none",
  cursor: "pointer",
  borderRadius: "0 16px 16px 0",
  "&:hover": {
    backgroundColor: "#F5F5F5",
  },
};
